import { TeamMentionContext } from './mentions/TeamMentionContext';
import { MentionContext } from './mentions/MentionDestination';
import { Link, useNavigate } from 'react-router-dom';
import { Intercom } from '@intercom/messenger-js-sdk';
import { useEffect } from 'react';
import { use100vh } from 'react-div-100vh';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import NavBar from '@/components/dashboards/navigation/NavBar';
import { DashboardFrame } from './DashboardFrame';
import { MobileTabBar } from './mobile/MobileTabBar';
import ConnectionChecker from './dashboards/navigation/ConnectionChecker';
import PhoneVerificationChecker from './dashboards/navigation/PhoneVerificationChecker';
import NavigationListener from './notifications/NavigationListener';
import { DevAuthBubble } from './sidebar/DevAuthBubble';
import { TooltipProvider } from './ui/tooltip';
import FloatingStatusBar from './voice/FloatingStatusBar';
import { WalkthroughProvider } from './walkthroughs/WalkthroughProvider';
import { WalkthroughMenuItem, WalkthroughWelcome } from './walkthroughs/WalkthroughsPage';
import { WalkthroughCoach } from './walkthroughs/WalkthroughCoach';
import { canExploreSignupDashboard, isPendingSignup, isSignupSendingBlocked } from './signup/activation';
import { SignupTextingBanner } from './signup/SignupTextingBanner';
import './signup/signup.css';
import { useIsMobile } from '@/hooks/use-mobile';
import { getShowNewLayout } from './sidebar/layout-preference';
import { INTERCOM_ALLOWED_RESELLER, shouldHideLauncher, syncLauncherVisibility } from './sidebar/support';

const Layout = ({ sidebarAddon, contentHeader }) => {
  // Default to showing the new layout if not set
  const storedLayout = localStorage.getItem('showNewLayout');
  if (storedLayout === null) {
    localStorage.setItem('showNewLayout', 'true');
  }
  const auth = useSelector((state) => state.auth);
  const pendingSignup = isPendingSignup(auth);
  const providersBlocked = isSignupSendingBlocked(auth);
  const exploringSignup = canExploreSignupDashboard(auth);
  // Phones always get the new layout; only it has the phone navigation.
  const showNewLayout = getShowNewLayout(auth);
  const suppressWalkthroughWelcome = exploringSignup || auth?.selfServeSignup?.environment === 'staging';
  const checkTextingStatus = auth?.selfServeSignup?.version === 1 && (exploringSignup || auth.selfServeSignup.status === 'active');
  const setupBar = checkTextingStatus ? <SignupTextingBanner key={`${auth._id}:${auth.user?._id}`} /> : null;
  const navigate = useNavigate();
  useEffect(() => {
    try {
    const pending = sessionStorage.getItem('pending-mention-path');
    if (!pendingSignup && auth?.user?._id && pending && /^\/(?:team-mentions|mentions)(?:\/[^/]+)?$/.test(pending)) { sessionStorage.removeItem('pending-mention-path'); navigate(pending, { replace: true }); }
    } catch { /* storage may be disabled */ }
  }, [auth?.user?._id, pendingSignup, navigate]);
  const hasVoiceProduct = auth?.products?.includes('voice');

  const height = use100vh();

  useEffect(() => {
    if (providersBlocked) return;
    // Initialize Intercom on Chekkit domains, or for the allowed reseller on any domain.
    const isIntercomAllowedReseller = auth?.reseller === INTERCOM_ALLOWED_RESELLER;
    const isChekkitDashboardDomain = [
      'dashboard.chekkit.io',
      'staging-dashboard.chekkit.io',
    ].includes(window.location.hostname);

    if (!isIntercomAllowedReseller && (auth?.reseller || !isChekkitDashboardDomain)) {
      return;
    }

    Intercom({
      app_id: 'w7pwcm7g',
      vertical_padding: 180,
      // Phones open support from the More drawer instead of a floating bubble.
      hide_default_launcher: shouldHideLauncher(),
      user_id: auth?.user?._id,
      name: auth?.user?.name,
      email: auth?.user?.email,
      created_at: auth?.user?.createdAt
        ? Math.floor(new Date(auth.user.createdAt).getTime() / 1000)
        : undefined,
    });
  }, [auth, providersBlocked]);

  // Rotating or resizing across the phone breakpoint shows or hides the bubble.
  const isMobile = useIsMobile();
  useEffect(() => {
    syncLauncherVisibility();
  }, [isMobile]);

  if (pendingSignup && !exploringSignup) return <DashboardFrame>{setupBar}<Outlet /></DashboardFrame>;

  // old layout (not used very much, can ignore, don't need to change)
  if (!showNewLayout) {
    return (
      <div className="flex flex-col" style={{ height: height - 2 }}>
        <TooltipProvider>
          <NavigationListener />
      <MentionContext /><TeamMentionContext />
          <NavBar />
          <div className="px-4 py-2 text-right text-sm"><Link to="/walkthroughs">Walkthroughs</Link></div>
          {contentHeader}
          {setupBar}
          <Outlet />
        </TooltipProvider>
      </div>
    );
  }

  // new layout (can be improved, but works for now)
  return (
    <DashboardFrame sidebarAddon={sidebarAddon} userMenuAddon={<WalkthroughMenuItem />} mobileNav={<MobileTabBar />} runtimeServices={<>
      <NavigationListener />
      <MentionContext /><TeamMentionContext />
      <NavBar />
      {!providersBlocked && <PhoneVerificationChecker />}
      {!providersBlocked && <ConnectionChecker />}
      {!providersBlocked && hasVoiceProduct && <FloatingStatusBar />}
      <DevAuthBubble />
    </>}>
      {contentHeader}
      {setupBar}
      {!suppressWalkthroughWelcome && <WalkthroughWelcome />}
      <Outlet />
      <WalkthroughCoach />
    </DashboardFrame>
  );
};

export default function AuthenticatedLayout(props) {
  return <WalkthroughProvider guidesEnabled={localStorage.getItem('showNewLayout') !== 'false' || getShowNewLayout()}><Layout {...props} /></WalkthroughProvider>;
}
